// SchedulePicker.jsx (MUI)
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Stack,
  Button,
  CircularProgress,
  Alert,
  Tooltip,
  Typography,
  InputBase,
  IconButton,
} from '@mui/material';
import {
  activateScheduleForSingleDevice,
  changeActiveScheduleId,
  changeSelectedScheduleId,
  getRecommendation,
  saveRecommendationSchedule,
  fetchUserSchedules,
  deleteScheduleById,
} from 'stores/sensor/controllersSlice';
import { brandColors } from 'types/colors';
import AddIcon from '@mui/icons-material/Add';
import { useParams } from 'react-router-dom';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { styled, keyframes } from '@mui/material/styles';
import GlobalStyles from '@mui/material/GlobalStyles';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CustomSnackbar from 'components/CustomSnackbar';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useNavigate } from 'react-router-dom';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { fetchControllerSchedules } from 'stores/sensor/controllersSlice';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { fetchCurrentData } from 'stores/sensor/controllersSlice';
import { ControllerCurrentDataFields } from 'types/ControllerFields.js';

const ACTIVE_LABEL = (
  <Box
    component="span"
    sx={{
      position: 'absolute',
      top: -14,
      right: -8,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: brandColors.GREEN,
      color: '#fff',
      px: 0.7,
      py: 0.2,
      borderRadius: 1,
      fontSize: '0.75rem',
      fontWeight: 600,
      letterSpacing: '0.05em',
      lineHeight: 1.5,
      textAlign: 'center',
      pointerEvents: 'none',
    }}>
    ACTIVE
  </Box>
);

const rotate = keyframes`
  from { --border-angle: 0deg; }
  to   { --border-angle: 360deg; }
`;

const solinButton = styled(Button)(({ theme }) => ({
  '--border-angle': '0deg',
  position: 'relative',
  borderRadius: 8,
  padding: '0.5rem 1rem',
  border: '2px solid transparent',
  color: brandColors.BLUE,
  background: `linear-gradient(#fff, #fff) padding-box,
     conic-gradient(
       from var(--border-angle),
       ${brandColors.BLACK},
       ${brandColors.BLUE},
       ${brandColors.TURQUOISE},
       #fff,
       ${brandColors.BLACK}
     ) border-box`,
  animation: `${rotate} 3s linear infinite`,
}));

function SelectableScheduleButton({
  scheduleId,
  label,
  activeScheduleId,
  newScheduleMode,
  onSelect,
  onActivate,
  currControlMode,
}) {
  // const activeScheduleId = useSelector(s => s.controllers.activeScheduleId);
  const selectedScheduleId = useSelector(s => s.controllers.selectedScheduleId);
  const isSelected = scheduleId === selectedScheduleId;

  const isActiveSchedule = scheduleId === activeScheduleId;
  const scheduleNameColor = isSelected
    ? '#fff'
    : isActiveSchedule
      ? brandColors.GREEN
      : brandColors.BLUE;
  const [showActivatePrompt, setShowActivatePrompt] = useState(false);
  const labelMatch = String(label || '').match(/^(.*?)(\s*\(\d+\))$/);
  const scheduleLabel = labelMatch ? labelMatch[1] : label;
  const scheduleNumber = labelMatch ? labelMatch[2] : '';

  const handleClick = () => {
    if (newScheduleMode) return;
    onSelect?.(scheduleId);
  };

  useEffect(() => {
    if (!isSelected || isActiveSchedule || currControlMode === 'schedule') {
      setShowActivatePrompt(false);
    }
  }, [currControlMode, isActiveSchedule, isSelected]);

  const handleActivate = event => {
    event.stopPropagation();
    if (newScheduleMode) return;
    if (currControlMode !== 'schedule') {
      setShowActivatePrompt(true);
      return;
    }
    onActivate?.(scheduleId);
  };

  const handleConfirmNo = event => {
    event.stopPropagation();
    setShowActivatePrompt(false);
  };

  const handleConfirmYes = event => {
    event.stopPropagation();
    setShowActivatePrompt(false);
    onActivate?.(scheduleId);
  };

  return (
    <Stack spacing={0.5} alignItems="center" sx={{ minHeight: '5.5rem' }}>
      <Button
        size="small"
        color="inherit"
        disabled={newScheduleMode}
        onClick={handleClick}
        sx={{
          position: 'relative',
          maxWidth: '14rem',
          minHeight: '3rem',
          px: 2,
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          textAlign: 'center',
          gap: 0.5,
          border: isActiveSchedule
            ? `4px solid ${brandColors.GREEN}`
            : `2px solid ${brandColors.BLUE}`,
          '&:hover': {
            border: isActiveSchedule
              ? `4px solid ${brandColors.GREEN}`
              : `2px solid ${brandColors.BLUE}`,
            backgroundColor: brandColors.BLUE,
            color: '#fff',
            '& .schedule-name': {
              color: '#fff',
            },
          },
          color: isSelected ? '#fff' : brandColors.BLUE,
          backgroundColor: isSelected ? brandColors.BLUE : '#fff',
        }}
        aria-pressed={isSelected}>
        {isActiveSchedule && ACTIVE_LABEL}
        {isSelected ? (
          <RadioButtonCheckedIcon sx={{ fontSize: 18, flexShrink: 0 }} />
        ) : (
          <RadioButtonUncheckedIcon sx={{ fontSize: 18, flexShrink: 0 }} />
        )}
          <Box
            component="span"
            className="schedule-name"
            sx={{
              display: 'flex',
              alignItems: 'center',
              minWidth: 0,
              lineHeight: 1.2,
              color: scheduleNameColor,
            }}>
            <Box
              component="span"
              sx={{
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
              {scheduleLabel}
            </Box>
            {scheduleNumber && (
              <Box component="span" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                {scheduleNumber}
              </Box>
            )}
          </Box>
      </Button>
      {isSelected && !isActiveSchedule && (
        <>
          <Button
            color="inherit"
            size="small"
            disabled={newScheduleMode}
            onClick={handleActivate}
            startIcon={<PlayArrowIcon sx={{ scale: '1.8', fontSize: 18 }} />}
            sx={{
              display: 'flex',
              alignItems: 'center',
              lineHeight: 1,
              minHeight: '2.25rem',
              px: 1.5,
              color: brandColors.GREEN,
              '&:hover': {
                backgroundColor: brandColors.GREEN,
                color: '#fff',
              },
            }}>
            Activate
          </Button>
          {showActivatePrompt && (
            <Box sx={{ mt: 0.5, maxWidth: '14rem', textAlign: 'center' }}>
              <Typography sx={{ fontSize: '.75rem', color: brandColors.BLUE, fontWeight: '500' }}>
                Switch to schedule Mode for this controller?
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 0.5 }}>
                <Button
                  size="small"
                  color="inherit"
                  onClick={handleConfirmYes}
                  sx={{
                    color: brandColors.GREEN,
                    border: `2px solid #fff`,
                    '&:hover': {
                      border: `2px solid ${brandColors.GREEN}`,
                    },
                  }}>
                  Yes
                </Button>
                <Button
                  size="small"
                  color="inherit"
                  onClick={handleConfirmNo}
                  sx={{
                    border: `2px solid #fff`,
                    color: brandColors.RED,
                    '&:hover': {
                      border: `2px solid ${brandColors.RED}`,
                    },
                  }}>
                  No
                </Button>
              </Stack>
            </Box>
          )}
        </>
      )}
    </Stack>
  );
}

const NESTED_SCHEDULE_KEYS = [
  'schedule',
  'schedules',
  'saveResult',
  'activateResult',
  'data',
  'result',
];

const extractScheduleId = payload => {
  if (!payload) return null;
  if (typeof payload === 'string' || typeof payload === 'number') return payload;

  if (Array.isArray(payload)) {
    for (const entry of payload) {
      const id = extractScheduleId(entry);
      if (id) return id;
    }
    return null;
  }

  if (typeof payload === 'object') {
    if (payload.id || payload.schedule_id) {
      return payload.id ?? payload.schedule_id;
    }
    for (const key of NESTED_SCHEDULE_KEYS) {
      if (payload[key]) {
        const nestedId = extractScheduleId(payload[key]);
        if (nestedId) return nestedId;
      }
    }
  }

  return null;
};

export default function SchedulePicker({
  deviceId,
  deviceSchedules = [],
  newScheduleMode,
  setNewScheduleMode,
  isRecommendationChanged,
  setIsRecommendationChanged,
  triggerSaveSchedule,
  isRecommendationInEditMode,
  setIsRecommendationInEditMode,
  tempRecommendation,
  setTempRecommendation,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const status = useSelector(s => s.controllers.schedulesStatus || 'idle');
  const error = useSelector(s => s.controllers.schedulesError || null);
  const currentMeasurements = useSelector(state => state.controllers.currentMeasurements);
  const selectedScheduleId = useSelector(s => s.controllers.selectedScheduleId);
  const activeScheduleId = useSelector(s => s.controllers.activeScheduleId);
  const [isRecommendationGenerated, setIsRecommendationGenerated] = useState(false);
  const [generatedRecommendationId, setGeneratedRecommendationId] = useState(null);
  const [recommendationLoading, setRecommendationLoading] = useState(false);
  const [isRecommendationSaved, setIsRecommendationSaved] = useState(false);
  const [recommendationName, setRecommendationName] = useState('RECOMMENDATION');
  const recommendationNameInputRef = useRef(null);
  const [snack, setSnack] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const autoSelectSchedule = useCallback(
    (response, { markRecommendation = false } = {}) => {
      const newScheduleId = extractScheduleId(response);
      if (!newScheduleId) return;
      // dispatch(changeSelectedScheduleId(newScheduleId));
      if (markRecommendation) {
        setGeneratedRecommendationId(newScheduleId);
        setIsRecommendationGenerated(true);
      }
    },
    [dispatch, setGeneratedRecommendationId, setIsRecommendationGenerated],
  );

  useEffect(() => {
    if (
      isRecommendationGenerated &&
      isRecommendationInEditMode &&
      recommendationNameInputRef.current
    ) {
      recommendationNameInputRef.current.focus();
      recommendationNameInputRef.current.select();
    }
  }, [isRecommendationGenerated, isRecommendationInEditMode]);

  useEffect(() => {
    // Cleanup recommendation when switching away
    if (activeScheduleId !== 0 && tempRecommendation) {
      setTempRecommendation(null);
      // setGeneratedRecommendationId(null);
      setIsRecommendationSaved(false);
      setIsRecommendationInEditMode(false);
    }
  }, [activeScheduleId]);

  const helperMessage = useMemo(() => {
    if (!deviceSchedules.length && !isRecommendationGenerated)
      return "You don't have any schedules yet. Create a schedule to begin controlling your devices.";

    // Don't show "please select" message if we have a generated recommendation
    if (isRecommendationGenerated && activeScheduleId === 0) {
      return ''; // or return a helpful message like "Review the AI-generated recommendation below"
    }

    if (
      !activeScheduleId ||
      (typeof activeScheduleId === 'string' && /[a-zA-Z]/.test(activeScheduleId))
    ) {
      return 'Please select a schedule to apply it to the selected controller.';
    }

    return '';
  }, [deviceSchedules, activeScheduleId, isRecommendationGenerated]);

  const selectedSchedule = useMemo(() => {
    if (selectedScheduleId === 0 && tempRecommendation) return tempRecommendation;
    return (
      deviceSchedules.find(schedule => schedule.id === selectedScheduleId) ||
      deviceSchedules.find(schedule => schedule.id === activeScheduleId) ||
      null
    );
  }, [deviceSchedules, selectedScheduleId, activeScheduleId, tempRecommendation]);

  if (status === 'loading') {
    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading schedules…</Typography>
      </Stack>
    );
  }

  if (status === 'failed') {
    return <Alert severity="error">Error: {String(error)}</Alert>;
  }

  const handleGetRecommendation = async () => {
    try {
      if (isRecommendationGenerated) return;
      setRecommendationLoading(true);
      const recommendedSchedule = await dispatch(getRecommendation(deviceId)).unwrap();
      setTempRecommendation(recommendedSchedule);
      setIsRecommendationGenerated(true);
      setGeneratedRecommendationId(0);
      setRecommendationName('new recommendation');
      setIsRecommendationInEditMode(false);
      setIsRecommendationChanged?.(false);
      // dispatch(changeActiveScheduleId(0));
      dispatch(changeSelectedScheduleId(0));
    } catch (e) {
    } finally {
      setRecommendationLoading(false);
    }
  };

  const RecommendationButton = isRecommendationGenerated ? solinButton : Button;
  const recommendationButtonSx = {
    visibility: isRecommendationSaved ? 'hidden' : 'visible',
    p: '1rem',
    width: '17rem',
    minWidth: '1rem',
    maxWidth: '17rem',
    height: '3rem',
    minHeight: '3rem',
    maxHeight: '3rem',
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
    color: brandColors.BLUE,
    ...(isRecommendationGenerated
      ? {}
      : {
          border: `2px solid ${brandColors.BLUE}`,
          '&:hover': { border: `2px solid ${brandColors.BLUE}` },
        }),
  };

  const handleRecommendationButtonClick = () => {
    if (isRecommendationGenerated && generatedRecommendationId !== null) {
      // dispatch(changeActiveScheduleId(generatedRecommendationId));
      dispatch(changeSelectedScheduleId(generatedRecommendationId));
      return;
    }

    if (!recommendationLoading) {
      handleGetRecommendation();
    }
  };

  const renderRecommendationButtonLabel = () => {
    if (recommendationLoading) {
      return 'Generating...';
    }

    if (!isRecommendationGenerated) {
      return 'GENERATE RECOMMENDATION';
    }

    if (isRecommendationInEditMode) {
      return (
        <InputBase
          className="afwafwawaf"
          value={recommendationName}
          onChange={handleRecommendationNameChange}
          inputRef={recommendationNameInputRef}
          onClick={event => event.stopPropagation()}
          onFocus={event => event.stopPropagation()}
          onMouseDown={event => event.stopPropagation()}
          inputProps={{
            maxLength: 30,
            'aria-label': 'Recommendation schedule name',
          }}
          sx={{
            flex: 1,
            fontWeight: 600,
            fontSize: '.8rem',
            color: 'inherit',
            height: '3rem',
            minHeight: '3rem',
            maxHeight: '3rem',
          }}
        />
      );
    }

    return recommendationName;
  };

  const isRecommendationSelected =
    generatedRecommendationId !== null && selectedScheduleId === generatedRecommendationId;

  const handleRecommendationNameChange = event => {
    setRecommendationName(event.target.value);
  };

  const handleSaveRecommendationSchedule = () => {
    // TODO: address case when isRecommendationChanged is false

    const trimmedName = (recommendationName || null).trim();
    // scheduleName is now optional - backend will generate default if not provided
    if (trimmedName) {
      setRecommendationName(trimmedName);
    }

    if (isRecommendationChanged) {
      // new schedule based on recommendation
      const scheduleData = { scheduleName: trimmedName };
      const result = triggerSaveSchedule?.('schedule-picker', scheduleData);
      if (result?.then) {
        result
          .then(payload => {
            autoSelectSchedule(payload, { markRecommendation: false }); // false because it's now a real schedule
            dispatch(fetchUserSchedules());
            setIsRecommendationSaved(true);
            setTempRecommendation(null);
            setIsRecommendationGenerated(false);
            setGeneratedRecommendationId(null);
          })
          .catch(() => {});
      }
    } else {
      // save recommendation as suggested
      const scheduleName = trimmedName;
      dispatch(saveRecommendationSchedule({ scheduleName, deviceId }))
        .unwrap()
        .then(async res => {
          autoSelectSchedule(res, { markRecommendation: false }); // false because it's now a real schedule
          const activated = await handleScheduleActivate(res.id);
          if (!activated) return;

          // Refresh only after activation finishes. Refreshing earlier can return
          // the previous active schedule and overwrite the new radio selection.
          await dispatch(fetchControllerSchedules({ deviceId, includePatterns: true })).unwrap();
          dispatch(changeSelectedScheduleId(res.id));
          setIsRecommendationSaved(true);
          setTempRecommendation(null);
          setIsRecommendationGenerated(false);
          setGeneratedRecommendationId(null);
          setSnack({
            open: true,
            message: 'Saved Recommendation Schedule',
            severity: 'success',
          });
          setNewScheduleMode(false);
        })
        .catch(err =>
          setSnack({
            open: true,
            message: err?.message || err || 'Error saving Recommendation',
            severity: 'error',
          }),
        );
    }

    setIsRecommendationInEditMode(false);
    setIsRecommendationChanged?.(false);
  };

  const handleScheduleSelect = scheduleId => {
    dispatch(changeSelectedScheduleId(scheduleId));
  };

  const handleScheduleActivate = async scheduleId => {
    dispatch(changeSelectedScheduleId(scheduleId));

    if (!deviceId) {
      setSnack({
        open: true,
        message: 'Unable to activate schedule: missing device ID',
        severity: 'error',
      });
      return false;
    }

    try {
      await dispatch(
        activateScheduleForSingleDevice({
          scheduleId,
          deviceId,
        }),
      )
        .unwrap()
        .then(res => {
          dispatch(changeActiveScheduleId(scheduleId));
          setSnack({
            open: true,
            message: 'Schedule activated successfully',
            severity: 'success',
          });
          dispatch(
            fetchCurrentData({
              fields: Object.values(ControllerCurrentDataFields).map(f => f.db_col_name),
              controller_id: deviceId,
            }),
          );
        });
      return true;
    } catch (error) {
      setSnack({
        open: true,
        message: error?.message || 'Failed to activate schedule',
        severity: 'error',
      });
      return false;
    }
  };

  const handleEditSchedule = () => {
    if (selectedScheduleId !== generatedRecommendationId)
      navigate(`/manage-schedules/${selectedScheduleId}`);
    else {
      setIsRecommendationInEditMode(o => !o);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        minWidth: '68rem',
        maxWidth: '68rem',
        flexWrap: 'wrap',
      }}>
      <CustomSnackbar
        {...snack}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        duration={4000}
        vertical="top"
        horizontal="left"
        color="#fff"
      />
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        <Button
          variant="inherit"
          disabled={newScheduleMode}
          onClick={() => setNewScheduleMode(true)}
          sx={{
            border: '2px solid #fff',
            color: brandColors.BLUE,
            '&:focus': {
              color: brandColors.BLUE,
            },
            '&:hover': { border: '2px solid blue' },
            paddingY: '.9rem',
          }}>
          <AddIcon sx={{ scale: '2', marginRight: '.5rem' }}></AddIcon>NEW SCHEDULE
        </Button>
        <Button
          // disabled={
          //   recommendationLoading || newScheduleMode || (!isRecommendationGenerated && selectedScheduleId !== 0)
          // }
          disabled={typeof selectedScheduleId !== 'number' || newScheduleMode}
          onClick={() => handleEditSchedule()}
          variant="inherit"
          sx={{
            border: '2px solid #fff',
            color: brandColors.BLUE,
            '&:focus': {
              color: brandColors.BLUE,
            },
            '&:hover': { border: '2px solid blue' },
          }}>
          {isRecommendationInEditMode ? (
            <>
              <ExpandLessIcon sx={{ scale: '2', marginRight: '.5rem' }}></ExpandLessIcon>CLOSE
            </>
          ) : (
            <>
              <EditIcon sx={{ scale: '1.5', marginRight: '.5rem' }}></EditIcon>Edit selected
              schedule
            </>
          )}
        </Button>
      </Stack>
      {(deviceSchedules.length > 0 || isRecommendationGenerated) && (
        <Typography sx={{ fontSize: '1rem', fontWeight: '500', marginTop: '1rem' }}>
          select and activate a schedule
        </Typography>
      )}
      <Stack direction="row" useFlexGap flexWrap="wrap" sx={{ mt: 1, columnGap: 2.5, rowGap: 0.5 }}>
        {deviceSchedules.map(schedule => {
          const label = schedule.schedule_name || String(schedule.id);

          return (
            <Box key={schedule.id} sx={{ position: 'relative', display: 'inline-block' }}>
              <Tooltip title={label}>
                <SelectableScheduleButton
                  scheduleId={schedule.id}
                  label={label}
                  activeScheduleId={activeScheduleId}
                  newScheduleMode={newScheduleMode}
                  onSelect={handleScheduleSelect}
                  onActivate={handleScheduleActivate}
                  currControlMode={currentMeasurements.control}
                />
              </Tooltip>
            </Box>
          );
        })}
      </Stack>

      {!isRecommendationSaved && (
        <Box sx={{ mt: '.5rem', width: '100%', display: 'flex', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {isRecommendationGenerated && (
            <GlobalStyles
              styles={`
                @property --border-angle {
                  syntax: "<angle>";
                  initial-value: 0deg;
                  inherits: false;
                }
              `}
            />
            )}
            <RecommendationButton
            onClick={handleRecommendationButtonClick}
            variant="inherit"
            disabled={recommendationLoading || newScheduleMode}
            sx={recommendationButtonSx}>
            {isRecommendationGenerated &&
              (isRecommendationSelected ? (
                <RadioButtonCheckedIcon sx={{ fontSize: 18, flexShrink: 0 }} />
              ) : (
                <RadioButtonUncheckedIcon sx={{ fontSize: 18, flexShrink: 0 }} />
              ))}

            {renderRecommendationButtonLabel()}
            {recommendationLoading ? (
              <CircularProgress size={20} thickness={5} />
            ) : isRecommendationGenerated ? null : (
              <AutoAwesomeIcon sx={{ scale: '2' }}></AutoAwesomeIcon>
            )}
          </RecommendationButton>
            {isRecommendationGenerated && (
            <>
              <Button
                disabled={newScheduleMode}
                onClick={handleSaveRecommendationSchedule}
                sx={{ color: '#000000c6', marginLeft: '.5rem', fontSize: '.85rem' }}
                variant="inherit">
                <SaveIcon sx={{ scale: '1.7', marginRight: '.7rem' }}></SaveIcon>SAVE & ACTIVATE
              </Button>
            </>
            )}
          </Box>
        </Box>
      )}

      {/* <Box sx={{ display: 'flex', gap: 2 }}> */}

      {/* <Button onClick={() => setEditScheduleMode(true)} sx={{ p: '0', pr: '1rem', mt: '.5rem' }}>
          <EditIcon sx={{ scale: '.4' }}></EditIcon>EDIT SCHEDULE
        </Button> */}
      {/* </Box> */}
      {helperMessage && (
        <Typography
          variant="caption"
          sx={{ mt: 2, color: '#202020b5', fontSize: '1rem', fontWeight: 500 }}>
          {helperMessage}
        </Typography>
      )}
    </Box>
  );
}
